import { useState, useEffect } from 'react';

type ViewType = 'config' | 'run' | 'equipment';

interface UsePersistentViewStateProps {
  sessionId: string;
  defaultView?: ViewType;
}

export const usePersistentViewState = ({
  sessionId,
  defaultView = 'config',
}: UsePersistentViewStateProps): [ViewType, (view: ViewType) => void] => {
  const storageKey = `sessionView-${sessionId}`;

  // Initialize state from localStorage or use default
  const [viewType, setViewType] = useState<ViewType>(() => {
    if (typeof window === 'undefined') return defaultView;

    try {
      const savedView = localStorage.getItem(storageKey);
      if (savedView && ['config', 'run', 'equipment'].includes(savedView)) {
        return savedView as ViewType;
      }
    } catch (error) {
      console.warn('Failed to load saved view state:', error);
    }
    return defaultView;
  });

  // Custom setter that also saves to localStorage
  const setPersistentViewType = (newView: ViewType) => {
    setViewType(newView);
    try {
      localStorage.setItem(storageKey, newView);
    } catch (error) {
      console.warn('Failed to save view state:', error);
    }
  };

  // Clean up old localStorage entries for sessions that no longer exist
  useEffect(() => {
    try {
      const keys = Object.keys(localStorage);
      const sessionViewKeys = keys.filter((key) => key.startsWith('sessionView-'));

      // Keep only the most recent 50 session view preferences
      if (sessionViewKeys.length > 50) {
        const sortedKeys = sessionViewKeys.sort((a, b) => {
          const timeA = localStorage.getItem(`${a}-timestamp`) || '0';
          const timeB = localStorage.getItem(`${b}-timestamp`) || '0';
          return parseInt(timeB) - parseInt(timeA);
        });

        // Remove old entries
        sortedKeys.slice(50).forEach((key) => {
          localStorage.removeItem(key);
          localStorage.removeItem(`${key}-timestamp`);
        });
      }

      // Update timestamp for current session
      localStorage.setItem(`${storageKey}-timestamp`, Date.now().toString());
    } catch (error) {
      console.warn('Failed to clean up localStorage:', error);
    }
  }, [storageKey]);

  return [viewType, setPersistentViewType];
};
